import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cancelPayment,
  confirmFreePayment,
  createCheckout,
  previewCoupons,
  type CheckoutResult,
  type PaymentQuote,
} from '../services/payment.api';
import { submitCheckoutForm } from '../utils/submitCheckoutForm';

const MAX_CODES = 5;

export type PurchaseTarget = {
  kind: 'token_pack' | 'package';
  itemPublicId: string;
  billingPeriod: 'month' | 'year' | 'one_time';
};

interface Options {
  target: PurchaseTarget | null;
  initialCoupon?: string | null;
  onFreeActivated: () => void;
}

/** Step 1 of checkout (token pack or package): quote with coupons, then create the order and hand over to SePay. */
export function usePurchaseCheckout({ target, initialCoupon, onFreeActivated }: Options) {
  const [codes, setCodes] = useState<string[]>([]);
  const [quote, setQuote] = useState<PaymentQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [quoteChanged, setQuoteChanged] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<CheckoutResult | null>(null);
  const requestId = useRef(0);

  const runPreview = useCallback(
    async (nextCodes: string[]) => {
      if (!target) return;
      const id = ++requestId.current;
      setLoading(true);
      setErrorCode(null);
      setErrorMessage(null);
      const res = await previewCoupons({
        kind: target.kind,
        itemPublicId: target.itemPublicId,
        billingPeriod: target.billingPeriod,
        codes: nextCodes,
      });
      if (id !== requestId.current) return;
      setLoading(false);
      if (res.success && res.data) {
        setQuote(res.data);
        setQuoteChanged(false);
      } else {
        setErrorCode(res.errorCode ?? 'ERROR');
        setErrorMessage(res.message ?? res.error ?? null);
      }
    },
    [target]
  );

  // Reset and quote whenever a pack is opened (deep-link ?coupon= prefills the box).
  useEffect(() => {
    requestId.current += 1;
    setQuote(null);
    setPendingOrder(null);
    setQuoteChanged(false);
    setErrorCode(null);
    setErrorMessage(null);
    setPaying(false);
    const start = initialCoupon ? [initialCoupon.trim().toUpperCase()] : [];
    setCodes(start);
    if (target) void runPreview(start);
  }, [target, initialCoupon, runPreview]);

  const addCode = useCallback(
    (raw: string) => {
      const code = raw.trim().toUpperCase().replace(/\s+/g, '');
      if (!code || codes.includes(code) || codes.length >= MAX_CODES) return;
      const next = [...codes, code];
      setCodes(next);
      void runPreview(next);
    },
    [codes, runPreview]
  );

  const removeCode = useCallback(
    (code: string) => {
      const next = codes.filter((item) => item !== code);
      setCodes(next);
      void runPreview(next);
    },
    [codes, runPreview]
  );

  const proceed = useCallback(
    (order: CheckoutResult) => {
      if (order.requiresPayment && order.checkout) {
        submitCheckoutForm(order.checkout);
        return;
      }
      void confirmFreePayment(order.publicId).then((res) => {
        setPaying(false);
        if (res.success) onFreeActivated();
        else {
          setErrorCode(res.errorCode ?? 'ERROR');
          setErrorMessage(res.message ?? res.error ?? null);
        }
      });
    },
    [onFreeActivated]
  );

  const pay = useCallback(async () => {
    if (!target || !quote) return;
    setPaying(true);
    setErrorCode(null);
    setErrorMessage(null);
    const res = await createCheckout({
      kind: target.kind,
      itemPublicId: target.itemPublicId,
      billingPeriod: target.billingPeriod,
      couponCodes: codes,
      expectedAmountMinor: quote.amountMinor,
    });
    if (!res.success || !res.data) {
      setPaying(false);
      if (res.errorCode === 'QUOTE_CHANGED' && res.data) {
        setQuote(res.data as unknown as PaymentQuote);
        setQuoteChanged(true);
        return;
      }
      setErrorCode(res.errorCode ?? 'ERROR');
      setErrorMessage(res.message ?? res.error ?? null);
      return;
    }
    if (res.data.reused) {
      // An unpaid order already exists: let the user continue it or start over.
      setPaying(false);
      setPendingOrder(res.data);
      return;
    }
    proceed(res.data);
  }, [target, quote, codes, proceed]);

  const continuePending = useCallback(() => {
    if (!pendingOrder) return;
    setPaying(true);
    proceed(pendingOrder);
  }, [pendingOrder, proceed]);

  const cancelPending = useCallback(async () => {
    if (!pendingOrder) return;
    setPaying(true);
    const res = await cancelPayment(pendingOrder.publicId);
    setPaying(false);
    if (res.success) {
      setPendingOrder(null);
      void runPreview(codes);
    } else {
      setErrorCode(res.errorCode ?? 'ERROR');
      setErrorMessage(res.message ?? res.error ?? null);
    }
  }, [pendingOrder, codes, runPreview]);

  return {
    codes,
    quote,
    loading,
    paying,
    errorCode,
    errorMessage,
    quoteChanged,
    pendingOrder,
    maxCodes: MAX_CODES,
    addCode,
    removeCode,
    pay,
    continuePending,
    cancelPending,
  };
}
